import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@mui/material/InputAdornment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Grid from '../../../components/Grid/Grid';
import Button from '../../../components/Button/Button';
import InputField from '../../../components/InputField/InputField';
import { DialogContext } from '../../../contexts/DialogContext';
import { dialogActions } from '../../../reducers/dialogReducer';
import Dialog from '../../../components/Dialog/Dialog';
import DialogTitle from '../../../components/DialogTitle/DialogTitle';
import useForm from '../../../hooks/useForm';
import DialogContent from '../../../components/DialogContent/DialogContent';
import IconButton from '../../../components/IconButton/IconButton';
import {
  precheckPowderBatchTemperatureFile,
  addPowderBatchTemperature,
} from '../../../services/settings-config';
import useCancelToken from '../../../hooks/useCancelToken';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';
import Typography from '../../../components/Typography/Typography';
import useStyles from '../useStyles';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { downloadFile } from '../../../Utils/fileUtil';

const PowderBatchTemperatureCreateDialog = ({ isOpen, onClose, onSubmitSuccess }) => {
  const { classes } = useStyles();
  const { newCancelToken } = useCancelToken();
  const initialForm = {
    powderBatch: '',
    configFile: null,
    configFileName: '',
  };
  const { formData, setFormData, formErr, setFormErr, resetFormErr } = useForm(initialForm);
  const [precheckResult, setPrecheckResult] = useState([]);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [precheckError, setPrecheckError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [showStatus, setShowStatus] = useState(false);
  const snackbarTimer = useRef(null);
  const { dialogDispatch } = useContext(DialogContext);
  const configFileNameRef = useRef(null);

  const openSnackbar = () => {
    setShowStatus(true);
    clearTimeout(snackbarTimer.current);
    snackbarTimer.current = setTimeout(() => {
      setShowStatus(false);
      clearTimeout(snackbarTimer.current);
    }, 3000);
  };

  const handleAttachFileClick = (e) => {
    e.stopPropagation();
    configFileNameRef.current.click();
  };

  const handleConfigFileChange = (e) => {
    setFormData({
      ...formData,
      configFileName: e.target.files[0].name,
      configFile: e.target.files[0],
    });
    setFormErr({ ...formErr, configFileName: '', configFile: '' });
  };

  const validationForm = () => {
    if (formData.powderBatch.length === 0) {
      setFormErr({ ...formErr, powderBatch: '請輸入試燒環編號' });
      return false;
    }
    if (formData.configFile === null) {
      setFormErr({ ...formErr, configFile: '請選擇檔案' });
      return false;
    }
    return true;
  };

  const handleDownloadTemplate = () => {
    downloadFile('/temperature_config_sample.xls');
  };

  const handleClose = () => {
    resetFormErr();
    setFormData(initialForm);
    setPrecheckResult([]);
    setPrecheckError('');
    setPrecheckLoading(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!validationForm()) return;
    const cancelToken = newCancelToken();
    await addPowderBatchTemperature({
      data: {
        deviceId: Number(formData.powderBatch),
        items: precheckResult.map((item) => ({
          size: Number(item.diameter),
          temperature: Number(item.temperature),
        })),
      },
      cancelToken,
      onSuccess: () => {
        resetFormErr();
        handleClose();
        onSubmitSuccess();
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        openSnackbar();
        setStatusMsg(
          noPrivilegeMsg || `新增溫度表失敗！請確認網路是否正常或聯繫軟體部門：${result}`
        );
      },
    });
  };

  const handleSubmitPrecheck = async () => {
    if (!validationForm()) return;
    const cancelToken = newCancelToken();
    setPrecheckLoading(true);
    setPrecheckError('');
    setPrecheckResult([]);
    await precheckPowderBatchTemperatureFile({
      data: {
        configFile: formData.configFile,
        deviceId: Number(formData.powderBatch),
      },
      cancelToken,
      onSuccess: (result) => {
        setPrecheckResult(result.precheckResult);
      },
      onError: (error) => {
        setPrecheckError(error);
      },
      onSettled: () => {
        setPrecheckLoading(false);
      },
    });
  };

  const handleCancel = () => {
    if (precheckResult.length > 0) {
      dialogDispatch({
        type: dialogActions.OPEN,
        config: {
          singleBtn: false,
          title: '確認離開？',
          message: '是否確認放棄新增此筆溫度表？',
          handleConfirm: handleClose,
        },
      });
    } else {
      handleClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_e, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="create-powder-batch-temperature-title">新增試燒環溫度表</DialogTitle>
      <DialogContent dividers sx={{ paddingTop: 3, paddingBottom: 3 }}>
        <Grid container direction="column" spacing={2}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={3}>
              <InputField
                id="powderBatch"
                label="試燒環編號"
                value={formData.powderBatch}
                variant="outlined"
                onChange={(e) => {
                  setFormData({ ...formData, powderBatch: e.target.value });
                  setFormErr({ ...formErr, powderBatch: '' });
                }}
                placeholder="請輸入試燒環編號"
                name="powderBatch"
                error={formErr.powderBatch}
                errormsg={formErr.powderBatch}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <input
                type="file"
                ref={configFileNameRef}
                onChange={handleConfigFileChange}
                style={{ display: 'none' }}
                accept=".xlsx,.csv,.xls"
              />
              <InputField
                id="configFile"
                label="上傳溫度表清單的Excel檔"
                value={formData.configFileName}
                variant="outlined"
                placeholder="請選擇檔案"
                name="configFile"
                error={formErr.configFile}
                errormsg={formErr.configFile}
                onClick={handleAttachFileClick}
                required
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="attach file" onClick={handleAttachFileClick}>
                        <AttachFileIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item container gap={1} alignItems="center" sx={{ marginTop: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleSubmitPrecheck}
                sx={{ height: 40 }}
              >
                上傳預覽
              </Button>
              <Button
                variant="outlined"
                color="grey"
                size="large"
                onClick={handleDownloadTemplate}
                sx={{ height: 40 }}
              >
                下載Excel範本
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={handleCancel}
                sx={{ height: 40 }}
              >
                取消
              </Button>
            </Grid>
          </Grid>
          <Grid
            item
            container
            direction="column"
            xs={12}
            sx={
              precheckLoading || precheckResult?.length > 0
                ? { paddingTop: 3, paddingBottom: 3 }
                : {}
            }
          >
            {precheckLoading && <CircularProgress />}
            {!precheckLoading && precheckError && (
              <Typography variant="body1" color="error" textAlign="center" sx={{ marginBottom: 3 }}>
                {`試燒環溫度上傳預覽失敗：${precheckError}`}
              </Typography>
            )}
            <Snackbar msg={statusMsg} type="error" open={showStatus} className={classes.snackbar} />
            {!precheckLoading && precheckResult?.length > 0 && (
              <Grid
                container
                item
                xs={12}
                spacing={2}
                direction="column"
                sx={{
                  paddingLeft: 2,
                  paddingRight: 2,
                  overflowY: 'scroll',
                  height: '400px',
                  flexWrap: 'nowrap',
                }}
              >
                <Typography variant="body1" fontWeight="bold" sx={{ marginBottom: 2 }}>
                  溫度表清單預覽
                </Typography>
                <Grid
                  container
                  item
                  xs={12}
                  sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    padding: 0,
                    marginBottom: 0.6,
                  })}
                >
                  <Grid item xs={4}>
                    <Typography variant="body1">Diameter (mm)</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant="body1">Temperature (°C)</Typography>
                  </Grid>
                </Grid>
                {precheckResult.map((item) => (
                  <Grid
                    container
                    item
                    xs={12}
                    key={item.diameter}
                    sx={(theme) => ({
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      padding: 0,
                      marginBottom: 0.6,
                    })}
                  >
                    <Grid item xs={4}>
                      <Typography variant="body1">{item.diameter}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1">{item.temperature}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      {precheckResult?.length > 0 && (
        <Grid container item xs={12} justifyContent="flex-end" sx={{ padding: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            確認上傳
          </Button>
        </Grid>
      )}
    </Dialog>
  );
}; // NewQCTypeAction
PowderBatchTemperatureCreateDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
};

export default PowderBatchTemperatureCreateDialog;
